/**
 * Fixed-timestep accumulator.
 *
 * Converts variable wall-clock frame gaps into a whole number of fixed
 * simulation ticks plus an interpolation alpha for rendering. It never
 * silently drops time: a gap that cannot be simulated within the catch-up
 * budget is reported as a stall so the caller can pause or invalidate the
 * active attempt (see ARCHITECTURE.md, "Stalls and suspension").
 */

export interface FrameAdvance {
  /** Number of fixed ticks the caller must simulate this frame. */
  ticks: number;
  /** Interpolation factor in [0, 1) between the previous and current tick. */
  alpha: number;
  /** True if wall-clock time was discarded instead of simulated. */
  stalled: boolean;
  /** Milliseconds discarded because of the stall (0 when not stalled). */
  discardedMs: number;
  /**
   * Wall-clock time (same clock as `nowMs`) at which the last of this frame's
   * ticks ends; tick k of n ends at lastTickEndMs − (n − 1 − k)·dtMs. Input
   * events up to that moment belong to this frame's ticks (docs/INPUT_TIMING.md).
   */
  lastTickEndMs: number;
}

export class FixedStepClock {
  readonly dt: number;
  readonly dtMs: number;
  private accumulatorMs = 0;
  private lastTimeMs: number | null = null;
  /** Monotonic count of ticks requested since construction. */
  private tickCount = 0;
  private readonly result: FrameAdvance = { ticks: 0, alpha: 0, stalled: false, discardedMs: 0, lastTickEndMs: 0 };

  constructor(
    tickRate: number,
    private readonly maxCatchUpTicks: number,
    private readonly stallThresholdMs: number,
  ) {
    if (!(tickRate > 0)) throw new Error('tickRate must be positive');
    this.dt = 1 / tickRate;
    this.dtMs = 1000 / tickRate;
  }

  get totalTicks(): number {
    return this.tickCount;
  }

  /** Forget the previous timestamp, e.g. after resuming from a pause. */
  reset(): void {
    this.lastTimeMs = null;
    this.accumulatorMs = 0;
  }

  /**
   * Advance by the wall-clock timestamp `nowMs` (monotonic, e.g. performance.now()).
   * The returned object is reused between calls.
   */
  advance(nowMs: number): Readonly<FrameAdvance> {
    const r = this.result;
    r.ticks = 0;
    r.stalled = false;
    r.discardedMs = 0;
    r.lastTickEndMs = nowMs;
    if (this.lastTimeMs === null) {
      this.lastTimeMs = nowMs;
      r.alpha = 0;
      return r;
    }
    let elapsed = nowMs - this.lastTimeMs;
    this.lastTimeMs = nowMs;
    if (!(elapsed > 0)) {
      // Non-monotonic or duplicate timestamp: simulate nothing.
      r.alpha = this.accumulatorMs / this.dtMs;
      return r;
    }
    if (elapsed > this.stallThresholdMs) {
      r.stalled = true;
      r.discardedMs = elapsed;
      elapsed = 0;
    }
    this.accumulatorMs += elapsed;
    let ticks = Math.floor(this.accumulatorMs / this.dtMs + 1e-9);
    if (ticks > this.maxCatchUpTicks) {
      r.stalled = true;
      r.discardedMs += (ticks - this.maxCatchUpTicks) * this.dtMs;
      ticks = this.maxCatchUpTicks;
      this.accumulatorMs = ticks * this.dtMs + (this.accumulatorMs % this.dtMs);
    }
    this.accumulatorMs -= ticks * this.dtMs;
    if (this.accumulatorMs < 0) this.accumulatorMs = 0;
    this.tickCount += ticks;
    r.ticks = ticks;
    r.alpha = Math.min(0.999999, this.accumulatorMs / this.dtMs);
    r.lastTickEndMs = nowMs - this.accumulatorMs;
    return r;
  }
}

/**
 * The input deadline for tick `k` of the `n` ticks a frame runs: events that
 * happened up to this wall-clock time belong to that tick. The last tick takes
 * everything that has arrived, so no event waits for a later frame (the
 * unsimulated remainder is under one tick). See docs/INPUT_TIMING.md.
 */
export function tickInputDeadline(adv: Readonly<FrameAdvance>, k: number, n: number, dtMs: number): number {
  return k === n - 1 ? Infinity : adv.lastTickEndMs - (n - 1 - k) * dtMs;
}
